/* Input park: the cell editor converts with the Table round (component-map.md). */
import { Box, Flex, FormControl, FormLabel, Input } from '@chakra-ui/react';
import { Text } from '@main/complykit/components/text';
import { Nullable } from '@main/shared/types';
import { FilterMode, isIsEmptyOrIsNotEmpty } from '@main/shared/url-helpers';
import { isEmpty } from '@main/shared/utils/is-empty';
import { CellContext, ColumnDef, Row, SortDirection, Table } from '@tanstack/react-table';
import equal from 'fast-deep-equal';
import {
  Dispatch,
  forwardRef,
  ReactElement,
  ReactNode,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import removeMarkdown from 'remove-markdown';
import { z } from 'zod/v4';

import { AutoResizeTextarea } from '../../autoresize-textarea';
import { EditablePopover } from '../../editable';
import { FloatingActionButtons } from '../../floating-action-buttons/floating-action-buttons';
import { MarkdownEditor } from '../../markdown/markdown-editor';
import { OverflowContainer } from '../../overflow-tooltip';
import { BulkChangeConfigBase, useSyncIsBulkLoading } from '../bulk-actions';
import { createFilterHelper } from '../filters';
import { FilterLabelRow } from '../filters/filter-label-row';
import { FilterModeSelector } from '../filters/filter-mode';
import { CellContainer } from '../shared/cell';
import { CanEditGuard, getEditable, ifBulkEditable } from '../table';
import {
  bulkScalarClearAction,
  BulkScalarEditAction,
  ScalarBulkClearButton,
} from './scalar-bulk-clear';

export type TextVariant = 'standard' | 'markdown';

type EditProps<TData> = {
  onChange: (row: Row<TData>, value: string) => void;
  canEditGuard?: CanEditGuard<TData, string | null | undefined>;
  defaultIsEditing?: (row: Row<TData>) => boolean;
};

export type BulkTextOnChange<TData> = (
  rows: Row<TData>[],
  value: string,
  action?: BulkScalarEditAction,
) => unknown;
export type UseBulkTextChangeHook<TData> = () => readonly [BulkTextOnChange<TData>, boolean];
export type TextCellSuffix<TData> = (row: Row<TData>, table: Table<TData>) => ReactNode;

type TextColumnConfig<TData, TFilter, TSort> = {
  id: string;
  header: string;
  edit?: EditProps<TData>;
  variant?: TextVariant;
  isMultiline?: boolean;
  suffix?: TextCellSuffix<TData>;
  getGlobalFilterCondition?(filterValue: string): Nullable<TFilter>;
  getColumnFilterCondition?(filterMode: FilterMode, filterValue: string): Nullable<TFilter>;
  getColumnSort?(sortDirection: SortDirection): TSort;
  bulkEdit?: {
    useBulkChange: UseBulkTextChangeHook<TData>;
    allowClearValue?: boolean;
    bulkInitialValue?: string;
  } & Omit<BulkChangeConfigBase, 'onClick' | 'wrapper'>;
};

const filterHelper = createFilterHelper({
  filterSchema: z.string(),
  filterFn,
});

function filterFn(
  value: string | undefined | null,
  filterValue: string | null,
  filterMode?: FilterMode,
): boolean {
  if (isIsEmptyOrIsNotEmpty(filterMode)) {
    if (filterMode === FilterMode.IsEmpty) {
      return isEmpty(value);
    }
    if (filterMode === FilterMode.IsNotEmpty) {
      return !isEmpty(value);
    }
  }
  if (!filterValue) {
    return true;
  }

  const isContainingValue = !!value?.toLowerCase().includes(filterValue.toLowerCase());

  if (filterMode === FilterMode.Excludes) {
    return !isContainingValue;
  }

  return isContainingValue;
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const anyValueChanged = <TData extends unknown>(
  rows: Row<TData>[],
  columnId: string,
  newValue: string | undefined,
): boolean =>
  rows.some((row) => {
    const value = row.getValue(columnId);
    const bothFalsy = !value && !newValue;
    return !bothFalsy && value !== newValue;
  });

export function text<TData, TFilter, TSort>() {
  return ({
    edit,
    variant = 'standard',
    isMultiline = false,
    suffix,
    bulkEdit,
    ...columnDef
  }: ColumnDef<TData, string | null | undefined> &
    TextColumnConfig<TData, TFilter, TSort>): ColumnDef<TData, string | null | undefined> => {
    const useBulkChange = bulkEdit?.useBulkChange;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
    const getBulkInitialValue = <T extends unknown>(
      rows: Row<T>[],
      columnId: string,
    ): string | undefined => {
      const [firstRow, ...restRows] = rows;
      if (!firstRow) return undefined;

      const value = firstRow.getValue<string>(columnId);
      return restRows.every((row) => equal(row.getValue(columnId), value)) ? value : undefined;
    };

    const editable = getEditable(edit);

    return {
      enableGlobalFilter: false,
      enableColumnFilter: false,
      enableSorting: false,
      sortDescFirst: false,

      ...columnDef,

      cell: (context) => {
        const value = context.getValue();
        const cellRender =
          columnDef.cell && typeof columnDef.cell === 'function' ? columnDef.cell : undefined;
        const cellSuffix = suffix?.(context.row, context.table);
        const reserveActionSpace = Boolean(cellSuffix);

        if (edit && editable(context.row.original, context)) {
          const forceEditViaFloatingButton = !!columnDef.meta?.cell?.forceEditViaFloatingButton;
          const showEditTrigger = forceEditViaFloatingButton || !!columnDef.meta?.cell?.onClick;
          return (
            <EditableCell
              edit={edit}
              context={context}
              variant={variant}
              isMultiline={isMultiline}
              render={cellRender}
              editIcon={showEditTrigger}
              suffix={cellSuffix}
              reserveActionSpace={reserveActionSpace}
            />
          );
        }

        return (
          <CellContainer cell={context.cell} p={0}>
            <OverflowContainer>
              <TextCellDisplay
                label={
                  variant === 'markdown' ? (
                    <MarkdownEditor
                      value={value ?? undefined}
                      config={{ editable: false }}
                      boxProps={{ sx: { '& > .editor-input': { fontSize: 'sm' } } }}
                    />
                  ) : (
                    value
                  )
                }
                suffix={cellSuffix}
              >
                <RenderText
                  value={value ?? undefined}
                  context={context}
                  variant={variant}
                  isMultiline={isMultiline}
                  render={cellRender}
                />
              </TextCellDisplay>
              <FloatingActionButtons
                isAttached={false}
                buttons={columnDef.meta?.cell?.actions?.(context.row) ?? []}
                reserveSpace={reserveActionSpace}
              />
            </OverflowContainer>
          </CellContainer>
        );
      },

      filterFn: filterHelper.filterFn,

      meta: {
        ...columnDef.meta,
        columnType: 'text',
        editable,
        name: columnDef.header,
        globalFilterFn: filterFn,
        // Tanstack table filters are untyped. Type safety is enforced via filter/column helpers
        getGlobalFilterCondition: columnDef.getGlobalFilterCondition as never,
        getColumnFilterCondition: columnDef.getColumnFilterCondition as never,
        getColumnSort: columnDef.getColumnSort as never,
        isMultiline,

        bulkEdit: ifBulkEditable(
          edit,
          bulkEdit,
          () =>
            bulkEdit &&
            useBulkChange && {
              ...bulkEdit,
              id: columnDef.id ?? '',
              editor: (editorRows, trigger, isInMenu, onOpenChange, labelledBy) => (
                <BulkTextEditor
                  id={columnDef.id ?? ''}
                  key={columnDef.id}
                  variant={variant}
                  trigger={trigger}
                  rows={editorRows}
                  useBulkChange={useBulkChange}
                  allowClearValue={bulkEdit.allowClearValue}
                  defaultValue={getBulkInitialValue(editorRows, columnDef.id ?? '')}
                  isInMenu={isInMenu}
                  onOpenChange={onOpenChange}
                  labelledBy={labelledBy}
                />
              ),
            },
        ),

        filter: {
          filterSchema: filterHelper.filterSchema,

          DisplayFilter: ({ column }) => {
            const { t } = useTranslation('ui');

            const mode = filterHelper.getFilterMode(column);
            return (
              <span>
                {isIsEmptyOrIsNotEmpty(mode) ? (
                  mode === FilterMode.IsEmpty ? (
                    t('table.filter.isEmpty')
                  ) : (
                    t('table.filter.isNotEmpty')
                  )
                ) : (
                  <>
                    {mode === FilterMode.Excludes && t('table.filter.notLabel')}
                    {filterHelper.getFilterValue(column)}
                  </>
                )}
              </span>
            );
          },

          EditFilter: ({ column }) => {
            const { t } = useTranslation('ui');

            const mode = filterHelper.getFilterMode(column);
            const isExcluding = mode === FilterMode.Excludes;
            const value = filterHelper.getFilterValue(column);
            const hasInput = !isIsEmptyOrIsNotEmpty(mode);
            return (
              <FormControl p={3}>
                <FormLabel fontWeight="medium" mb={hasInput ? undefined : 0}>
                  <FilterLabelRow label={columnDef.header}>
                    <FilterModeSelector {...filterHelper} column={column} />
                  </FilterLabelRow>
                </FormLabel>
                {hasInput && (
                  <Input
                    autoFocus
                    size="sm"
                    placeholder={t('table.column.text.filterPlacehoder')}
                    borderRadius="base"
                    value={value || ''}
                    aria-label={`${columnDef.header} ${
                      isExcluding ? t('table.filter.excludes') : t('table.filter.includes')
                    }`}
                    onChange={(event) =>
                      filterHelper.setFilterValue(column, event.target.value || null)
                    }
                  />
                )}
              </FormControl>
            );
          },
        },
      },
    };
  };
}

interface RenderTextProps {
  value: string | null | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  context: CellContext<any, string | null | undefined>;
  variant: TextVariant;
  isMultiline: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render?: (context: CellContext<any, string | null | undefined>) => JSX.Element;
}

export const markdownAsPlainText = (text?: string | null) =>
  removeMarkdown(text || '')
    .replace(/\s+/g, ' ')
    .trim();

const RenderText = forwardRef<HTMLDivElement, RenderTextProps>(
  ({ value, context, variant, isMultiline, render, ...props }, ref) => {
    return render ? (
      render(context)
    ) : (
      // The tooltip's trigger handlers and its ref must land on one element: Chakra
      // opens on the handlers but listens for pointerleave only on the ref.
      <Box py={4} px={2} whiteSpace={isMultiline ? 'normal' : undefined} ref={ref} {...props}>
        <Text size="inherit" isTruncated={!isMultiline}>
          {variant === 'markdown' ? markdownAsPlainText(value) : value}
        </Text>
      </Box>
    );
  },
);

interface TextCellDisplayProps {
  children: ReactElement;
  label: ReactNode;
  suffix?: ReactNode;
}

function TextCellDisplay({ children, label, suffix }: TextCellDisplayProps) {
  const text = (
    <OverflowContainer.Tooltip label={label} hasArrow placement="top" fontSize="sm" openDelay={500}>
      {children}
    </OverflowContainer.Tooltip>
  );

  if (!suffix) {
    return text;
  }

  return (
    <Flex flex="0 1 auto" minW={0} maxW="full" alignItems="center" gap={2} pr={1}>
      {text}
      {suffix}
    </Flex>
  );
}

type EditableCellProps<TData> = {
  edit: EditProps<TData>;
  context: CellContext<TData, string | null | undefined>;
  variant: TextVariant;
  isMultiline: boolean;
  editIcon?: boolean;
  suffix?: ReactNode;
  reserveActionSpace?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render?: (context: CellContext<any, string | null | undefined>) => JSX.Element;
};

function EditableCell<TData>({
  edit: { onChange, defaultIsEditing },
  context,
  variant,
  isMultiline,
  render,
  editIcon,
  suffix,
  reserveActionSpace,
}: EditableCellProps<TData>) {
  const cellValue = context.getValue();
  const [value, setValue] = useState(cellValue || '');
  useEffect(() => {
    setValue(cellValue || '');
  }, [cellValue]);

  const onCellUpdate = () => {
    if (value !== cellValue) {
      onChange(context.row, value);
      setValue(cellValue || ''); // reset to the actual value after update in case it was changed while editing
    }
  };

  const cellActions = context.cell.column.columnDef.meta?.cell?.actions?.(context.row) ?? [];

  const cellContent = (
    <CellContainer cell={context.cell} p={0}>
      <OverflowContainer>
        <TextCellDisplay
          label={
            variant === 'markdown' ? (
              <MarkdownEditor
                value={cellValue ?? undefined}
                config={{ editable: false }}
                boxProps={{ sx: { '& > .editor-input': { fontSize: 'sm' } } }}
              />
            ) : (
              cellValue
            )
          }
          suffix={suffix}
        >
          <RenderText
            value={cellValue ?? undefined}
            context={context}
            variant={variant}
            isMultiline={isMultiline}
            render={render}
          />
        </TextCellDisplay>
        {(editIcon || cellActions.length > 0) && (
          <EditablePopover.Trigger>
            <OverflowContainer.EditTrigger
              buttons={cellActions}
              hideEditIcon={!editIcon}
              reserveSpace={reserveActionSpace}
              tooltip={context.cell.column.columnDef.meta?.cell?.editTriggerTooltip}
            />
          </EditablePopover.Trigger>
        )}
      </OverflowContainer>
    </CellContainer>
  );

  return (
    <EditablePopover
      onClose={onCellUpdate}
      defaultIsEditing={defaultIsEditing && defaultIsEditing(context.row)}
    >
      <EditablePopover.Reference>
        {editIcon ? cellContent : <EditablePopover.Trigger>{cellContent}</EditablePopover.Trigger>}
      </EditablePopover.Reference>
      <EditablePopover.Content bgColor="gray.25" _dark={{ bgColor: 'gray.700' }}>
        {() => (
          <TextEditor
            variant={variant}
            defaultValue={cellValue ?? undefined}
            value={value}
            setValue={setValue}
          />
        )}
      </EditablePopover.Content>
    </EditablePopover>
  );
}
type BulkTextEditorProps<TData> = {
  id: string;
  variant: TextVariant;
  defaultValue?: string;
  trigger: JSX.Element;
  rows: Row<TData>[];
  useBulkChange: UseBulkTextChangeHook<TData>;
  allowClearValue?: boolean;
  isInMenu?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  labelledBy?: string;
};

function BulkTextEditor<TData>({
  id,
  variant,
  defaultValue = '',
  trigger,
  rows,
  useBulkChange,
  allowClearValue = false,
  isInMenu,
  onOpenChange,
  labelledBy,
}: BulkTextEditorProps<TData>) {
  const [value, setValue] = useState(defaultValue);
  const skipNextUpdateRef = useRef(false);
  useEffect(() => {
    skipNextUpdateRef.current = false;
    setValue(defaultValue);
  }, [defaultValue]);

  const [onChange, isLoading] = useBulkChange();
  useSyncIsBulkLoading(id, isLoading);

  const onUpdate = async () => {
    if (skipNextUpdateRef.current) {
      skipNextUpdateRef.current = false;
      return;
    }

    if (allowClearValue && value.trim() === '') {
      return;
    }

    if (anyValueChanged(rows, id ?? '', value) && value !== defaultValue) {
      await onChange(rows, value);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      skipNextUpdateRef.current = false;
    }
    onOpenChange?.(isOpen);
  };

  const handleClear = async (setOpen: (isOpen: boolean) => void) => {
    if (isLoading) {
      return;
    }

    skipNextUpdateRef.current = true;

    const didUpdate = await onChange(rows, value, bulkScalarClearAction);
    if (didUpdate !== false) {
      skipNextUpdateRef.current = true;
      setOpen(false);
      return;
    }

    skipNextUpdateRef.current = false;
  };

  return (
    <EditablePopover
      onClose={onUpdate}
      onOpenChange={handleOpenChange}
      openEvent={isInMenu ? 'mousedown' : undefined}
      placement={isInMenu ? 'left-start' : 'top-end'}
      closeOnBlur={false}
    >
      <EditablePopover.Trigger>{trigger}</EditablePopover.Trigger>
      <EditablePopover.Content
        bgColor="gray.25"
        _dark={{ bgColor: 'gray.700' }}
        offset={-1}
        data-testid={`bulk-editor-${id}`}
      >
        {(context) => (
          <TextEditor
            variant={variant}
            defaultValue={defaultValue}
            value={value}
            setValue={setValue}
            labelledBy={labelledBy}
            clearButton={
              allowClearValue ? (
                <ScalarBulkClearButton
                  isDisabled={isLoading}
                  onClick={() => void handleClear(context.setOpen)}
                />
              ) : undefined
            }
          />
        )}
      </EditablePopover.Content>
    </EditablePopover>
  );
}

type TextEditorProps = {
  id?: string;
  variant: TextVariant;
  value: string;
  defaultValue?: string;
  setValue: Dispatch<SetStateAction<string>>;
  labelledBy?: string;
  clearButton?: JSX.Element;
};
const TextEditor = ({
  id,
  variant,
  defaultValue,
  value,
  setValue,
  labelledBy,
  clearButton,
}: TextEditorProps) => {
  if (variant === 'markdown') {
    return (
      <Box
        py={4}
        px={2}
        outline="2px solid"
        outlineColor="chakra-border-color"
        position={clearButton ? 'relative' : undefined}
      >
        <MarkdownEditor
          defaultValue={defaultValue}
          config={{ editable: true }}
          onValueChange={(newValue) => setValue(newValue)}
          contentEditableProps={{
            autoFocus: true,
            onClick: (event) => event.stopPropagation(),
            style: { outline: 'none', fontSize: '12px' },
          }}
          aria-labelledby={labelledBy}
        />
        {clearButton}
      </Box>
    );
  }

  const textEditor = (
    <AutoResizeTextarea
      id={id}
      data-testid="resizable-input"
      aria-labelledby={labelledBy}
      autoFocus
      size="sm"
      variant="unstyled"
      py={4}
      pl={2}
      pr={clearButton ? 10 : 2}
      minH="50px"
      outline="2px solid"
      outlineColor="chakra-border-color"
      defaultValue={value}
      onChange={(event) => setValue(event.target.value)}
      blurOnEnter
      /* This stops triggering click event on cell itself when clicking on the textarea */
      onClick={(event) => event.stopPropagation()}
    />
  );

  return clearButton ? (
    <Box position="relative">
      {textEditor}
      {clearButton}
    </Box>
  ) : (
    textEditor
  );
};
